import * as THREE from "three";
import type { QualityLevel } from "../shared/types";
import {
  createCloudTexture,
  createGlowTexture,
  createPainterlyTexture,
  fractalNoise,
  mulberry32,
  randomPointInEllipse,
  setObjectShadow,
  type RandomSource,
} from "./procedural";

type Biome = "meadow" | "garden" | "workshop" | "sanctum" | "wild";

interface IslandSpec {
  id: string;
  label: string;
  x: number;
  y: number;
  z: number;
  radiusX: number;
  radiusZ: number;
  depth: number;
  seed: number;
  biome: Biome;
  decorative?: boolean;
}

interface BridgeSpec {
  start: THREE.Vector3;
  end: THREE.Vector3;
  width: number;
  unlockedOnly?: boolean;
}

interface FloatingPlatform {
  x: number;
  y: number;
  z: number;
  radius: number;
  unlockedOnly: boolean;
  mesh: THREE.Object3D;
}

interface DetailInstance {
  mesh: THREE.InstancedMesh;
  maximum: number;
}

interface HorizontalObstacle {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  minY: number;
  maxY: number;
}

interface AnimatedTower {
  id: string;
  label: string;
  group: THREE.Group;
  position: THREE.Vector3;
  rings: THREE.Mesh[];
  crystal: THREE.Mesh;
  glow: THREE.Sprite;
  light: THREE.PointLight;
  beam: THREE.Mesh;
  active: boolean;
}

interface AnimatedShard {
  id: string;
  label: string;
  group: THREE.Group;
  crystal: THREE.Mesh;
  position: THREE.Vector3;
  collected: boolean;
  phase: number;
}

interface Spinner {
  object: THREE.Object3D;
  axis: "x" | "y" | "z";
  speed: number;
}

export interface CoreInteraction {
  id: string;
  label: string;
  distance: number;
  position: THREE.Vector3;
}

export interface CollectedShard {
  id: string;
  label: string;
}

const ISLANDS: readonly IslandSpec[] = [
  {
    id: "windward-meadow",
    label: "起風草原",
    x: 0,
    y: 8,
    z: 0,
    radiusX: 58,
    radiusZ: 49,
    depth: 48,
    seed: 1103,
    biome: "meadow",
  },
  {
    id: "hanging-gardens",
    label: "垂瀑花園",
    x: 97,
    y: 22,
    z: -31,
    radiusX: 31,
    radiusZ: 26,
    depth: 37,
    seed: 2207,
    biome: "garden",
  },
  {
    id: "copperwing-workshop",
    label: "銅翼工坊",
    x: -90,
    y: 10,
    z: -29,
    radiusX: 29,
    radiusZ: 25,
    depth: 39,
    seed: 3319,
    biome: "workshop",
  },
  {
    id: "stargazer-sanctum",
    label: "星盤聖所",
    x: 5,
    y: 37,
    z: -96,
    radiusX: 29,
    radiusZ: 23,
    depth: 43,
    seed: 4421,
    biome: "sanctum",
  },
  {
    id: "far-wild-one",
    label: "遠方荒島",
    x: 155,
    y: 2,
    z: 82,
    radiusX: 28,
    radiusZ: 20,
    depth: 34,
    seed: 5519,
    biome: "wild",
    decorative: true,
  },
  {
    id: "far-wild-two",
    label: "遠方荒島",
    x: -172,
    y: 33,
    z: 64,
    radiusX: 24,
    radiusZ: 18,
    depth: 31,
    seed: 6619,
    biome: "wild",
    decorative: true,
  },
  {
    id: "far-wild-three",
    label: "遠方荒島",
    x: 112,
    y: 54,
    z: -150,
    radiusX: 19,
    radiusZ: 16,
    depth: 26,
    seed: 7723,
    biome: "wild",
    decorative: true,
  },
];

const PLAYABLE_ISLANDS = ISLANDS.filter((island) => !island.decorative);

const COLOR_SETS: Record<Biome, readonly string[]> = {
  meadow: ["#375f45", "#568056", "#7e9f65", "#a5b97a", "#d2c995"],
  garden: ["#315d4e", "#4f8061", "#74a66e", "#a7bd82", "#d8d2a4"],
  workshop: ["#4e5146", "#64644d", "#7c7656", "#a08c66", "#c8b485"],
  sanctum: ["#49635d", "#638078", "#819b87", "#aeb89b", "#d6cfac"],
  wild: ["#3b5d4c", "#567457", "#718665", "#9a9b72", "#bdb187"],
};

const ROCK_COLORS = ["#2e3f43", "#455254", "#62645b", "#786f5f", "#95856e"] as const;

function islandSurfaceHeight(spec: IslandSpec, worldX: number, worldZ: number): number | null {
  const localX = worldX - spec.x;
  const localZ = worldZ - spec.z;
  const normalized = (localX * localX) / (spec.radiusX * spec.radiusX)
    + (localZ * localZ) / (spec.radiusZ * spec.radiusZ);

  if (normalized > 1) return null;

  const edgeFade = THREE.MathUtils.clamp(1 - normalized, 0, 1);
  const broad = fractalNoise(localX + spec.x * 0.1, localZ + spec.z * 0.1, spec.seed) - 0.5;
  const rolling = Math.sin(localX * 0.075 + spec.seed) * Math.cos(localZ * 0.065) * 0.6;
  const plateau = Math.pow(edgeFade, 0.45) * (spec.biome === "sanctum" ? 2.1 : 1.4);
  return spec.y + 0.35 + plateau + broad * 2.4 * edgeFade + rolling * edgeFade;
}

function getIslandById(id: string): IslandSpec {
  const island = ISLANDS.find((candidate) => candidate.id === id);
  if (!island) throw new Error(`找不到島嶼：${id}`);
  return island;
}

function surfacePosition(islandId: string, x: number, z: number, lift = 0): THREE.Vector3 {
  const island = getIslandById(islandId);
  const y = islandSurfaceHeight(island, x, z) ?? island.y;
  return new THREE.Vector3(x, y + lift, z);
}

export class SkyWorld {
  readonly root = new THREE.Group();
  readonly cameraColliders: THREE.Object3D[] = [];
  readonly sun = new THREE.DirectionalLight(0xfff0c4, 3.25);
  readonly hemisphere = new THREE.HemisphereLight(0xc8f3ff, 0x5a5840, 1.85);

  private readonly scene: THREE.Scene;
  private readonly dailySeed: number;
  private readonly islandMeshes = new Map<string, THREE.Mesh>();
  private readonly bridges: BridgeSpec[] = [];
  private readonly floatingPlatforms: FloatingPlatform[] = [];
  private readonly detailInstances: DetailInstance[] = [];
  private readonly obstacles: HorizontalObstacle[] = [];
  private readonly towers = new Map<string, AnimatedTower>();
  private readonly shards = new Map<string, AnimatedShard>();
  private readonly spinners: Spinner[] = [];
  private readonly cloudPoints: THREE.Points;
  private readonly motes: THREE.Points;
  private readonly sanctumPath = new THREE.Group();
  private readonly sanctumBeacon: THREE.Mesh;
  private quality: QualityLevel;
  private sanctumUnlocked = false;
  private currentSunAngle = 0.55;

  private readonly stoneMaterial: THREE.MeshStandardMaterial;
  private readonly paleStoneMaterial: THREE.MeshStandardMaterial;
  private readonly copperMaterial: THREE.MeshStandardMaterial;
  private readonly darkMetalMaterial: THREE.MeshStandardMaterial;
  private readonly woodMaterial: THREE.MeshStandardMaterial;
  private readonly leafMaterial: THREE.MeshStandardMaterial;
  private readonly trunkMaterial: THREE.MeshStandardMaterial;

  constructor(
    scene: THREE.Scene,
    dailySeed: number,
    quality: QualityLevel,
    cloudDensity = 0.7,
  ) {
    this.scene = scene;
    this.dailySeed = dailySeed;
    this.quality = quality;
    this.root.name = "風核群島";
    this.scene.add(this.root);

    const stoneTexture = createPainterlyTexture(ROCK_COLORS, dailySeed ^ 0xa133, 256);
    stoneTexture.repeat.set(6, 6);
    const paleTexture = createPainterlyTexture(
      ["#8e967f", "#b2b59a", "#d3ccb0", "#e3d9bd", "#f0e7ca"],
      dailySeed ^ 0xb733,
      256,
    );
    paleTexture.repeat.set(3, 3);
    const woodTexture = createPainterlyTexture(
      ["#4a3528", "#684c33", "#86623f", "#a27a4d"],
      dailySeed ^ 0xc533,
      256,
    );
    woodTexture.repeat.set(4, 2);

    this.stoneMaterial = new THREE.MeshStandardMaterial({
      color: 0x7e7768,
      map: stoneTexture,
      roughness: 0.94,
      metalness: 0.02,
      flatShading: true,
    });
    this.paleStoneMaterial = new THREE.MeshStandardMaterial({
      color: 0xddd5b9,
      map: paleTexture,
      roughness: 0.88,
      metalness: 0.01,
    });
    this.copperMaterial = new THREE.MeshStandardMaterial({
      color: 0xb77742,
      roughness: 0.42,
      metalness: 0.72,
      emissive: 0x291305,
      emissiveIntensity: 0.12,
    });
    this.darkMetalMaterial = new THREE.MeshStandardMaterial({
      color: 0x334247,
      roughness: 0.35,
      metalness: 0.76,
    });
    this.woodMaterial = new THREE.MeshStandardMaterial({
      color: 0x8e6947,
      map: woodTexture,
      roughness: 0.92,
    });
    this.leafMaterial = new THREE.MeshStandardMaterial({
      color: 0x547e54,
      roughness: 0.9,
      flatShading: true,
    });
    this.trunkMaterial = new THREE.MeshStandardMaterial({
      color: 0x73563d,
      roughness: 1,
      flatShading: true,
    });

    this.createSky();
    this.createLighting();

    for (const island of ISLANDS) {
      this.createIsland(island);
      if (!island.decorative) this.decorateIsland(island);
    }

    this.createBridges();
    this.createVillage();
    this.createGardenLandmarks();
    this.createWorkshop();
    this.createSanctum();
    this.createCoreTowers();
    this.createShards();
    this.createFlyingLife();

    this.cloudPoints = this.createCloudLayer(cloudDensity);
    this.motes = this.createMotes();
    this.root.add(this.cloudPoints, this.motes);

    const beaconMaterial = new THREE.MeshBasicMaterial({
      color: 0x79f5d9,
      transparent: true,
      opacity: 0.15,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.sanctumBeacon = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 2.8, 86, 20, 1, true), beaconMaterial);
    this.sanctumBeacon.position.copy(surfacePosition("stargazer-sanctum", 5, -96, 43));
    this.sanctumBeacon.visible = false;
    this.root.add(this.sanctumBeacon);

    this.setQuality(quality);
  }

  groundHeightAt(worldX: number, worldZ: number): number | null {
    let highest: number | null = null;

    for (const island of PLAYABLE_ISLANDS) {
      const height = islandSurfaceHeight(island, worldX, worldZ);
      if (height !== null && (highest === null || height > highest)) highest = height;
    }

    for (const bridge of this.bridges) {
      if (bridge.unlockedOnly && !this.sanctumUnlocked) continue;
      const directionX = bridge.end.x - bridge.start.x;
      const directionZ = bridge.end.z - bridge.start.z;
      const lengthSquared = directionX * directionX + directionZ * directionZ;
      const projection = THREE.MathUtils.clamp(
        ((worldX - bridge.start.x) * directionX + (worldZ - bridge.start.z) * directionZ) / lengthSquared,
        0,
        1,
      );
      const nearestX = bridge.start.x + directionX * projection;
      const nearestZ = bridge.start.z + directionZ * projection;
      const distance = Math.hypot(worldX - nearestX, worldZ - nearestZ);
      if (distance <= bridge.width * 0.52) {
        const height = THREE.MathUtils.lerp(bridge.start.y, bridge.end.y, projection) + 0.18;
        if (highest === null || height > highest) highest = height;
      }
    }

    for (const platform of this.floatingPlatforms) {
      if (platform.unlockedOnly && !this.sanctumUnlocked) continue;
      if (Math.hypot(worldX - platform.x, worldZ - platform.z) <= platform.radius) {
        if (highest === null || platform.y > highest) highest = platform.y;
      }
    }

    return highest;
  }

  resolvePlayerMovement(
    previous: THREE.Vector2,
    desired: THREE.Vector2,
    feetY: number,
    radius = 0.48,
  ): THREE.Vector2 {
    const resolved = desired.clone();
    for (const obstacle of this.obstacles) {
      if (feetY > obstacle.maxY || feetY + 2.25 < obstacle.minY) continue;
      const minX = obstacle.minX - radius;
      const maxX = obstacle.maxX + radius;
      const minZ = obstacle.minZ - radius;
      const maxZ = obstacle.maxZ + radius;
      if (resolved.x <= minX || resolved.x >= maxX || resolved.y <= minZ || resolved.y >= maxZ) continue;

      const previousInsideX = previous.x > minX && previous.x < maxX;
      const previousInsideZ = previous.y > minZ && previous.y < maxZ;
      if (!previousInsideX) {
        resolved.x = previous.x <= minX ? minX : maxX;
      } else if (!previousInsideZ) {
        resolved.y = previous.y <= minZ ? minZ : maxZ;
      } else {
        const distances = [
          { axis: "x" as const, value: minX, distance: Math.abs(resolved.x - minX) },
          { axis: "x" as const, value: maxX, distance: Math.abs(maxX - resolved.x) },
          { axis: "z" as const, value: minZ, distance: Math.abs(resolved.y - minZ) },
          { axis: "z" as const, value: maxZ, distance: Math.abs(maxZ - resolved.y) },
        ].sort((a, b) => a.distance - b.distance);
        if (distances[0].axis === "x") resolved.x = distances[0].value;
        else resolved.y = distances[0].value;
      }
    }
    return resolved;
  }

  getRegionAt(position: THREE.Vector3): { id: string; label: string } {
    let nearest: IslandSpec | null = null;
    let nearestScore = Number.POSITIVE_INFINITY;

    for (const island of PLAYABLE_ISLANDS) {
      const score = Math.hypot(
        (position.x - island.x) / island.radiusX,
        (position.z - island.z) / island.radiusZ,
      );
      if (score < nearestScore) {
        nearestScore = score;
        nearest = island;
      }
    }

    if (nearest && nearestScore < 1.45) return { id: nearest.id, label: nearest.label };
    return { id: "skyway", label: "雲海航道" };
  }

  findCoreInteraction(position: THREE.Vector3, maximumDistance = 5.8): CoreInteraction | null {
    let nearest: CoreInteraction | null = null;
    for (const tower of this.towers.values()) {
      if (tower.active) continue;
      const distance = position.distanceTo(tower.position);
      if (distance <= maximumDistance && (!nearest || distance < nearest.distance)) {
        nearest = {
          id: tower.id,
          label: tower.label,
          distance,
          position: tower.position.clone(),
        };
      }
    }
    return nearest;
  }

  collectNearbyShards(position: THREE.Vector3, maximumDistance = 2.45): CollectedShard[] {
    const collected: CollectedShard[] = [];
    for (const shard of this.shards.values()) {
      if (shard.collected || position.distanceTo(shard.position) > maximumDistance) continue;
      shard.collected = true;
      shard.group.visible = false;
      collected.push({ id: shard.id, label: shard.label });
    }
    return collected;
  }

  activateCore(id: string): { label: string; checkpoint: THREE.Vector3 } | null {
    const tower = this.towers.get(id);
    if (!tower || tower.active) return null;
    this.setTowerActive(tower, true);
    return {
      label: tower.label,
      checkpoint: tower.position.clone().add(new THREE.Vector3(0, 0.35, 4.7)),
    };
  }

  applyProgress(activatedCoreIds: readonly string[], collectedShardIds: readonly string[]): void {
    for (const id of activatedCoreIds) {
      const tower = this.towers.get(id);
      if (tower) this.setTowerActive(tower, true);
    }
    for (const id of collectedShardIds) {
      const shard = this.shards.get(id);
      if (shard) {
        shard.collected = true;
        shard.group.visible = false;
      }
    }
    if (this.activeCoreCount === this.totalCoreCount) this.unlockSanctum();
  }

  unlockSanctum(): void {
    if (this.sanctumUnlocked) return;
    this.sanctumUnlocked = true;
    this.sanctumPath.visible = true;
    this.sanctumBeacon.visible = true;
  }

  setQuality(level: QualityLevel): void {
    this.quality = level;
    const ratio = level === "high" ? 1 : level === "medium" ? 0.64 : 0.34;
    for (const detail of this.detailInstances) {
      detail.mesh.count = Math.max(1, Math.floor(detail.maximum * ratio));
      detail.mesh.instanceMatrix.needsUpdate = true;
    }
    this.sun.castShadow = level !== "low";
    this.cloudPoints.material = this.cloudPoints.material;
    const cloudMaterial = this.cloudPoints.material as THREE.PointsMaterial;
    cloudMaterial.opacity = level === "low" ? 0.55 : level === "medium" ? 0.68 : 0.78;
  }

  update(delta: number, elapsed: number, playerPosition: THREE.Vector3): void {
    this.currentSunAngle += delta * 0.0018;
    const sunArc = 0.72 + Math.sin(this.currentSunAngle) * 0.18;
    this.sun.position.set(Math.cos(this.currentSunAngle) * 125, 118 * sunArc, Math.sin(this.currentSunAngle) * 92);
    this.sun.target.position.set(0, 4, -20);
    this.sun.color.setHSL(0.105, 0.62, 0.77 + Math.sin(this.currentSunAngle) * 0.04);

    this.cloudPoints.position.x = Math.sin(elapsed * 0.012) * 7;
    this.cloudPoints.position.z = (elapsed * 0.16) % 28;
    this.motes.rotation.y = elapsed * 0.007;

    for (const spinner of this.spinners) {
      spinner.object.rotation[spinner.axis] += delta * spinner.speed;
    }

    for (const tower of this.towers.values()) {
      tower.crystal.position.y = 6.5 + Math.sin(elapsed * 1.8 + tower.position.x) * 0.22;
      tower.glow.material.rotation = elapsed * 0.08;
      tower.rings.forEach((ring, index) => {
        ring.rotation.z += delta * (tower.active ? 0.38 : 0.08) * (index % 2 === 0 ? 1 : -1);
        ring.rotation.x = Math.sin(elapsed * 0.35 + index) * 0.28;
      });
      if (tower.active) {
        tower.light.intensity = 3.2 + Math.sin(elapsed * 3.2 + tower.position.z) * 0.45;
      }
    }

    for (const shard of this.shards.values()) {
      if (shard.collected) continue;
      shard.group.rotation.y += delta * 0.9;
      shard.group.position.y = shard.position.y + Math.sin(elapsed * 2.2 + shard.phase) * 0.32;
      shard.crystal.rotation.x = Math.sin(elapsed * 1.3 + shard.phase) * 0.24;
    }

    const distanceToSanctum = playerPosition.distanceTo(new THREE.Vector3(5, 40, -96));
    const beaconMaterial = this.sanctumBeacon.material as THREE.MeshBasicMaterial;
    beaconMaterial.opacity = THREE.MathUtils.lerp(0.1, 0.28, THREE.MathUtils.clamp(distanceToSanctum / 130, 0, 1));
    this.sanctumBeacon.rotation.y += delta * 0.08;
  }

  get totalCoreCount(): number {
    return this.towers.size;
  }

  get activeCoreCount(): number {
    return [...this.towers.values()].filter((tower) => tower.active).length;
  }

  get totalShardCount(): number {
    return this.shards.size;
  }

  get collectedShardCount(): number {
    return [...this.shards.values()].filter((shard) => shard.collected).length;
  }

  private createSky(): void {
    const skyGeometry = new THREE.SphereGeometry(430, 40, 24);
    const skyMaterial = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        topColor: { value: new THREE.Color(0x3f91bd) },
        horizonColor: { value: new THREE.Color(0xbce8e4) },
        bottomColor: { value: new THREE.Color(0xf1ddb0) },
        sunColor: { value: new THREE.Color(0xffefb2) },
        sunDirection: { value: new THREE.Vector3(-0.35, 0.5, -0.6).normalize() },
      },
      vertexShader: `
        varying vec3 vDirection;
        void main() {
          vDirection = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 horizonColor;
        uniform vec3 bottomColor;
        uniform vec3 sunColor;
        uniform vec3 sunDirection;
        varying vec3 vDirection;
        void main() {
          float h = vDirection.y * 0.5 + 0.5;
          vec3 lower = mix(bottomColor, horizonColor, smoothstep(0.0, 0.52, h));
          vec3 sky = mix(lower, topColor, smoothstep(0.48, 1.0, h));
          float sun = pow(max(dot(vDirection, sunDirection), 0.0), 96.0);
          float haze = pow(max(dot(vDirection, sunDirection), 0.0), 8.0) * 0.16;
          sky += sunColor * (sun * 1.1 + haze);
          gl_FragColor = vec4(sky, 1.0);
        }
      `,
    });
    const sky = new THREE.Mesh(skyGeometry, skyMaterial);
    sky.frustumCulled = false;
    this.scene.add(sky);
    this.scene.fog = new THREE.FogExp2(0xb8dfe0, 0.00225);

    const sunSprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: createGlowTexture("rgba(255,250,205,1)", "rgba(255,231,150,0)"),
        color: 0xfff0b8,
        transparent: true,
        opacity: 0.72,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    sunSprite.scale.setScalar(46);
    sunSprite.position.set(-125, 105, -185);
    this.scene.add(sunSprite);
  }

  private createLighting(): void {
    this.sun.position.set(-95, 118, 72);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.camera.left = -145;
    this.sun.shadow.camera.right = 145;
    this.sun.shadow.camera.top = 145;
    this.sun.shadow.camera.bottom = -145;
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 310;
    this.sun.shadow.bias = -0.00045;
    this.sun.shadow.normalBias = 0.035;
    this.sun.target.position.set(0, 4, -20);
    this.scene.add(this.sun, this.sun.target, this.hemisphere);
  }

  private createIsland(spec: IslandSpec): void {
    const rings = spec.decorative ? 16 : 34;
    const segments = spec.decorative ? 36 : 72;
    const positions: number[] = [];
    const colors: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    const palette = COLOR_SETS[spec.biome].map((color) => new THREE.Color(color));

    const centerHeight = islandSurfaceHeight(spec, spec.x, spec.z) ?? spec.y;
    positions.push(0, centerHeight - spec.y, 0);
    colors.push(palette[2].r, palette[2].g, palette[2].b);
    uvs.push(0.5, 0.5);

    for (let ring = 1; ring <= rings; ring += 1) {
      const radius = ring / rings;
      for (let segment = 0; segment < segments; segment += 1) {
        const angle = (segment / segments) * Math.PI * 2;
        const localX = Math.cos(angle) * spec.radiusX * radius;
        const localZ = Math.sin(angle) * spec.radiusZ * radius;
        const height = islandSurfaceHeight(spec, spec.x + localX, spec.z + localZ) ?? spec.y;
        positions.push(localX, height - spec.y, localZ);

        const noise = fractalNoise(localX, localZ, spec.seed + 99);
        const edge = Math.pow(radius, 3.2);
        const colorIndex = THREE.MathUtils.clamp(Math.floor((noise * 0.68 + edge * 0.62) * palette.length), 0, palette.length - 1);
        const color = palette[colorIndex];
        colors.push(color.r, color.g, color.b);
        uvs.push(localX / (spec.radiusX * 2) + 0.5, localZ / (spec.radiusZ * 2) + 0.5);
      }
    }

    for (let segment = 0; segment < segments; segment += 1) {
      indices.push(0, 1 + ((segment + 1) % segments), 1 + segment);
    }

    for (let ring = 1; ring < rings; ring += 1) {
      const currentStart = 1 + (ring - 1) * segments;
      const nextStart = 1 + ring * segments;
      for (let segment = 0; segment < segments; segment += 1) {
        const nextSegment = (segment + 1) % segments;
        const a = currentStart + segment;
        const b = nextStart + segment;
        const c = nextStart + nextSegment;
        const d = currentStart + nextSegment;
        indices.push(a, d, b, b, d, c);
      }
    }

    const topGeometry = new THREE.BufferGeometry();
    topGeometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    topGeometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    topGeometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
    topGeometry.setIndex(indices);
    topGeometry.computeVertexNormals();
    topGeometry.computeBoundingSphere();

    const topTexture = createPainterlyTexture(COLOR_SETS[spec.biome], spec.seed, 256);
    topTexture.repeat.set(4, 4);
    const topMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      map: topTexture,
      roughness: 0.96,
      metalness: 0,
    });
    const top = new THREE.Mesh(topGeometry, topMaterial);
    top.position.set(spec.x, spec.y, spec.z);
    top.receiveShadow = true;
    top.castShadow = false;
    top.name = `${spec.label}地表`;
    this.root.add(top);
    this.islandMeshes.set(spec.id, top);
    if (!spec.decorative) this.cameraColliders.push(top);

    const cliffGeometry = new THREE.CylinderGeometry(1, 0.08, spec.depth, spec.decorative ? 32 : 64, 7, false);
    cliffGeometry.scale(spec.radiusX * 0.99, 1, spec.radiusZ * 0.99);
    const cliff = new THREE.Mesh(cliffGeometry, this.stoneMaterial);
    cliff.position.set(spec.x, spec.y - spec.depth / 2 + 0.2, spec.z);
    cliff.castShadow = true;
    cliff.receiveShadow = true;
    cliff.name = `${spec.label}浮岩`;
    this.root.add(cliff);

    const random = mulberry32(spec.seed ^ this.dailySeed);
    const shardGeometry = new THREE.ConeGeometry(1, 8, 6, 1);
    for (let index = 0; index < (spec.decorative ? 3 : 7); index += 1) {
      const angle = random() * Math.PI * 2;
      const radius = 0.25 + random() * 0.55;
      const rock = new THREE.Mesh(shardGeometry, this.stoneMaterial);
      rock.scale.set(1.2 + random() * 2.4, 1.1 + random() * 2.2, 1.2 + random() * 2.4);
      rock.position.set(
        spec.x + Math.cos(angle) * spec.radiusX * radius,
        spec.y - spec.depth * (0.38 + random() * 0.45),
        spec.z + Math.sin(angle) * spec.radiusZ * radius,
      );
      rock.rotation.set(random() * 0.4, random() * Math.PI, random() * 0.35);
      rock.castShadow = true;
      this.root.add(rock);
    }
  }

  private decorateIsland(spec: IslandSpec): void {
    const random = mulberry32(spec.seed ^ this.dailySeed ^ 0x916d);
    const grassMaximum = spec.biome === "workshop" ? 170 : spec.biome === "sanctum" ? 220 : 430;
    const grassGeometry = new THREE.ConeGeometry(0.07, 0.78, 3, 1);
    grassGeometry.translate(0, 0.39, 0);
    const grassMaterial = new THREE.MeshStandardMaterial({
      color: spec.biome === "garden" ? 0x79a968 : 0x6f965e,
      roughness: 1,
      side: THREE.DoubleSide,
    });
    const grass = new THREE.InstancedMesh(grassGeometry, grassMaterial, grassMaximum);
    const dummy = new THREE.Object3D();

    for (let index = 0; index < grassMaximum; index += 1) {
      const point = randomPointInEllipse(random, spec.radiusX, spec.radiusZ, 0.1);
      const worldX = spec.x + point.x;
      const worldZ = spec.z + point.y;
      const ground = islandSurfaceHeight(spec, worldX, worldZ) ?? spec.y;
      const scale = 0.7 + random() * 1.4;
      dummy.position.set(worldX, ground, worldZ);
      dummy.rotation.set(0, random() * Math.PI, (random() - 0.5) * 0.2);
      dummy.scale.set(scale, scale * (0.75 + random() * 0.6), scale);
      dummy.updateMatrix();
      grass.setMatrixAt(index, dummy.matrix);
    }
    grass.instanceMatrix.needsUpdate = true;
    grass.castShadow = false;
    grass.receiveShadow = true;
    grass.frustumCulled = true;
    this.root.add(grass);
    this.detailInstances.push({ mesh: grass, maximum: grassMaximum });

    const treeMaximum = spec.biome === "workshop" ? 7 : spec.biome === "sanctum" ? 10 : 22;
    const trunkGeometry = new THREE.CylinderGeometry(0.22, 0.34, 3.2, 6);
    trunkGeometry.translate(0, 1.6, 0);
    const crownGeometry = new THREE.IcosahedronGeometry(1.7, 1);
    crownGeometry.translate(0, 3.8, 0);
    const trunks = new THREE.InstancedMesh(trunkGeometry, this.trunkMaterial, treeMaximum);
    const crowns = new THREE.InstancedMesh(crownGeometry, this.leafMaterial, treeMaximum);
    const treePositions: THREE.Vector2[] = [];

    for (let index = 0; index < treeMaximum; index += 1) {
      let point = randomPointInEllipse(random, spec.radiusX, spec.radiusZ, 0.2);
      let attempts = 0;
      while (treePositions.some((other) => other.distanceTo(point) < 5.2) && attempts < 20) {
        point = randomPointInEllipse(random, spec.radiusX, spec.radiusZ, 0.2);
        attempts += 1;
      }
      treePositions.push(point.clone());
      const worldX = spec.x + point.x;
      const worldZ = spec.z + point.y;
      const ground = islandSurfaceHeight(spec, worldX, worldZ) ?? spec.y;
      const scale = 0.72 + random() * 0.7;
      dummy.position.set(worldX, ground, worldZ);
      dummy.rotation.set(0, random() * Math.PI, (random() - 0.5) * 0.08);
      dummy.scale.set(scale, scale, scale);
      dummy.updateMatrix();
      trunks.setMatrixAt(index, dummy.matrix);
      crowns.setMatrixAt(index, dummy.matrix);
    }
    trunks.instanceMatrix.needsUpdate = true;
    crowns.instanceMatrix.needsUpdate = true;
    trunks.castShadow = true;
    crowns.castShadow = true;
    trunks.receiveShadow = true;
    crowns.receiveShadow = true;
    this.root.add(trunks, crowns);
    this.detailInstances.push(
      { mesh: trunks, maximum: treeMaximum },
      { mesh: crowns, maximum: treeMaximum },
    );

    const rockMaximum = 26;
    const rockGeometry = new THREE.DodecahedronGeometry(0.8, 0);
    const rocks = new THREE.InstancedMesh(rockGeometry, this.stoneMaterial, rockMaximum);
    for (let index = 0; index < rockMaximum; index += 1) {
      const point = randomPointInEllipse(random, spec.radiusX, spec.radiusZ, 0.12);
      const worldX = spec.x + point.x;
      const worldZ = spec.z + point.y;
      const ground = islandSurfaceHeight(spec, worldX, worldZ) ?? spec.y;
      const scale = 0.35 + random() * 1.2;
      dummy.position.set(worldX, ground + scale * 0.35, worldZ);
      dummy.rotation.set(random() * Math.PI, random() * Math.PI, random() * Math.PI);
      dummy.scale.set(scale * (0.75 + random() * 0.5), scale, scale * (0.75 + random() * 0.5));
      dummy.updateMatrix();
      rocks.setMatrixAt(index, dummy.matrix);
    }
    rocks.instanceMatrix.needsUpdate = true;
    rocks.castShadow = true;
    rocks.receiveShadow = true;
    this.root.add(rocks);
    this.detailInstances.push({ mesh: rocks, maximum: rockMaximum });
  }

  private createBridges(): void {
    const meadow = getIslandById("windward-meadow");
    const garden = getIslandById("hanging-gardens");
    const workshop = getIslandById("copperwing-workshop");

    const bridgeToGarden: BridgeSpec = {
      start: surfacePosition(meadow.id, 51, -19, 0.2),
      end: surfacePosition(garden.id, 68, -25, 0.2),
      width: 3.6,
    };
    const bridgeToWorkshop: BridgeSpec = {
      start: surfacePosition(meadow.id, -52, -18, 0.2),
      end: surfacePosition(workshop.id, -63, -23, 0.2),
      width: 3.4,
    };
    this.buildBridge(bridgeToGarden, 0xc49b65);
    this.buildBridge(bridgeToWorkshop, 0x896748);

    this.sanctumPath.name = "星盤聖所風路";
    this.sanctumPath.visible = false;
    this.root.add(this.sanctumPath);
    const start = new THREE.Vector3(1, 14, -49);
    const end = new THREE.Vector3(5, 39, -72);
    const platformMaterial = new THREE.MeshStandardMaterial({
      color: 0x9cb6a5,
      emissive: 0x1c756d,
      emissiveIntensity: 0.35,
      roughness: 0.62,
      metalness: 0.12,
    });
    for (let index = 0; index < 7; index += 1) {
      const t = index / 6;
      const x = THREE.MathUtils.lerp(start.x, end.x, t) + Math.sin(index * 1.9) * 2.5;
      const y = THREE.MathUtils.lerp(start.y, end.y, t);
      const z = THREE.MathUtils.lerp(start.z, end.z, t);
      const radius = index % 2 === 0 ? 3.1 : 2.6;
      const platform = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius * 0.7, 1.25, 7), platformMaterial);
      platform.position.set(x, y - 0.62, z);
      platform.rotation.y = index * 0.7;
      platform.castShadow = true;
      platform.receiveShadow = true;
      this.sanctumPath.add(platform);

      const rune = new THREE.Mesh(
        new THREE.TorusGeometry(radius * 0.44, 0.08, 6, 24),
        new THREE.MeshBasicMaterial({ color: 0x75f2d8, transparent: true, opacity: 0.68 }),
      );
      rune.rotation.x = Math.PI / 2;
      rune.position.set(x, y + 0.04, z);
      this.sanctumPath.add(rune);
      this.spinners.push({ object: rune, axis: "z", speed: index % 2 === 0 ? 0.22 : -0.22 });
      this.floatingPlatforms.push({ x, y, z, radius: radius * 0.86, unlockedOnly: true, mesh: platform });
    }
  }

  private buildBridge(spec: BridgeSpec, ropeColor: number): void {
    this.bridges.push(spec);
    const group = new THREE.Group();
    const direction = spec.end.clone().sub(spec.start);
    const horizontalLength = Math.hypot(direction.x, direction.z);
    const steps = Math.max(5, Math.ceil(horizontalLength / 1.1));
    const angle = Math.atan2(direction.x, direction.z);
    const plankGeometry = new THREE.BoxGeometry(spec.width, 0.28, 0.9);

    for (let index = 0; index <= steps; index += 1) {
      const t = index / steps;
      const plank = new THREE.Mesh(plankGeometry, this.woodMaterial);
      plank.position.lerpVectors(spec.start, spec.end, t);
      plank.position.y += Math.sin(t * Math.PI) * -0.28;
      plank.rotation.set((Math.random() - 0.5) * 0.025, angle + (Math.random() - 0.5) * 0.02, (Math.random() - 0.5) * 0.025);
      plank.castShadow = true;
      plank.receiveShadow = true;
      group.add(plank);
    }

    const ropeMaterial = new THREE.LineBasicMaterial({ color: ropeColor, transparent: true, opacity: 0.78 });
    for (const side of [-1, 1]) {
      const points: THREE.Vector3[] = [];
      for (let index = 0; index <= steps; index += 1) {
        const t = index / steps;
        const center = new THREE.Vector3().lerpVectors(spec.start, spec.end, t);
        const sideVector = new THREE.Vector3(Math.cos(angle), 0, -Math.sin(angle)).multiplyScalar(spec.width * 0.47 * side);
        center.add(sideVector);
        center.y += 1.15 - Math.sin(t * Math.PI) * 0.42;
        points.push(center);
      }
      const rail = new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), ropeMaterial);
      group.add(rail);
    }
    this.root.add(group);
    this.cameraColliders.push(...group.children.filter((child): child is THREE.Mesh => child instanceof THREE.Mesh));
  }

  private createVillage(): void {
    const houses = [
      [-24, 11],
      [-15, 19],
      [21, 18],
      [30, 5],
      [-31, -5],
      [15, -27],
    ] as const;
    houses.forEach(([x, z], index) => this.createHouse(x, z, index));

    const windmillBase = surfacePosition("windward-meadow", 34, -18, 0);
    const tower = new THREE.Mesh(new THREE.CylinderGeometry(2.2, 3.4, 9.2, 10), this.paleStoneMaterial);
    tower.position.copy(windmillBase).add(new THREE.Vector3(0, 4.6, 0));
    tower.castShadow = true;
    tower.receiveShadow = true;
    this.root.add(tower);

    const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.48, 1, 12), this.copperMaterial);
    hub.rotation.x = Math.PI / 2;
    hub.position.copy(windmillBase).add(new THREE.Vector3(0, 7.1, -2.4));
    this.root.add(hub);
    const blades = new THREE.Group();
    blades.position.copy(hub.position).add(new THREE.Vector3(0, 0, -0.55));
    const bladeMaterial = new THREE.MeshStandardMaterial({ color: 0xeadcad, roughness: 0.8, side: THREE.DoubleSide });
    for (let index = 0; index < 6; index += 1) {
      const blade = new THREE.Mesh(new THREE.BoxGeometry(0.7, 4.8, 0.12), bladeMaterial);
      blade.position.y = 2.55;
      const pivot = new THREE.Group();
      pivot.rotation.z = (index / 6) * Math.PI * 2;
      pivot.add(blade);
      blades.add(pivot);
    }
    setObjectShadow(blades);
    this.root.add(blades);
    this.spinners.push({ object: blades, axis: "z", speed: 0.28 });

    const pond = new THREE.Mesh(
      new THREE.CircleGeometry(7.5, 48),
      new THREE.MeshPhysicalMaterial({
        color: 0x5eafb5,
        transparent: true,
        opacity: 0.68,
        roughness: 0.12,
        metalness: 0.05,
        transmission: 0.18,
      }),
    );
    const pondGround = this.groundHeightAt(11, 27) ?? 9;
    pond.rotation.x = -Math.PI / 2;
    pond.position.set(11, pondGround + 0.07, 27);
    this.root.add(pond);
  }

  private createHouse(x: number, z: number, index: number): void {
    const ground = this.groundHeightAt(x, z) ?? 9;
    const group = new THREE.Group();
    group.position.set(x, ground, z);
    group.rotation.y = index * 1.37;
    const width = 4.8 + (index % 2) * 0.8;
    const wall = new THREE.Mesh(new THREE.BoxGeometry(width, 3.8, 4.2), this.paleStoneMaterial);
    wall.position.y = 1.9;
    const roofMaterial = new THREE.MeshStandardMaterial({
      color: index % 3 === 0 ? 0x9e684b : index % 3 === 1 ? 0x55746f : 0xb58958,
      roughness: 0.9,
      flatShading: true,
    });
    const roof = new THREE.Mesh(new THREE.ConeGeometry(width * 0.75, 2.3, 4), roofMaterial);
    roof.rotation.y = Math.PI / 4;
    roof.position.y = 4.85;
    const door = new THREE.Mesh(new THREE.BoxGeometry(1.05, 2.05, 0.15), this.woodMaterial);
    door.position.set(0, 1.05, -2.17);
    const windowMaterial = new THREE.MeshStandardMaterial({ color: 0x9ce4d8, emissive: 0x3fae9e, emissiveIntensity: 0.5 });
    const window = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 0.12), windowMaterial);
    window.position.set(-1.4, 2.2, -2.2);
    group.add(wall, roof, door, window);
    setObjectShadow(group);
    this.root.add(group);
    this.cameraColliders.push(wall, roof);
    const halfWidth = width * 0.48;
    this.obstacles.push({
      minX: x - halfWidth,
      maxX: x + halfWidth,
      minZ: z - 2.05,
      maxZ: z + 2.05,
      minY: ground,
      maxY: ground + 5.8,
    });
  }

  private createGardenLandmarks(): void {
    const waterfallMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x83dbe1,
      transparent: true,
      opacity: 0.42,
      roughness: 0.12,
      transmission: 0.45,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const locations = [
      [122, -31, 25],
      [91, -53, 18],
      [72, -39, 14],
    ] as const;
    locations.forEach(([x, z, length], index) => {
      const top = this.groundHeightAt(x, z) ?? 22;
      const waterfall = new THREE.Mesh(new THREE.PlaneGeometry(3.8 + index, length, 1, 8), waterfallMaterial);
      waterfall.position.set(x, top - length / 2, z);
      waterfall.rotation.y = index * 0.9;
      this.root.add(waterfall);
    });

    const archGround = this.groundHeightAt(106, -17) ?? 23;
    this.createAncientArch(new THREE.Vector3(106, archGround, -17), 0.5, 1.15);
    this.createAncientArch(new THREE.Vector3(84, this.groundHeightAt(84, -42) ?? 23, -42), -0.65, 0.9);
  }

  private createAncientArch(position: THREE.Vector3, rotationY: number, scale: number): void {
    const group = new THREE.Group();
    group.position.copy(position);
    group.rotation.y = rotationY;
    group.scale.setScalar(scale);
    const left = new THREE.Mesh(new THREE.BoxGeometry(1.15, 7.5, 1.15), this.paleStoneMaterial);
    const right = left.clone();
    left.position.set(-3.1, 3.75, 0);
    right.position.set(3.1, 3.75, 0);
    const top = new THREE.Mesh(new THREE.BoxGeometry(7.4, 1.2, 1.35), this.paleStoneMaterial);
    top.position.y = 7.45;
    const ring = new THREE.Mesh(new THREE.TorusGeometry(2.2, 0.32, 8, 36, Math.PI), this.copperMaterial);
    ring.position.y = 4.8;
    ring.rotation.z = Math.PI;
    group.add(left, right, top, ring);
    setObjectShadow(group);
    this.root.add(group);
    this.cameraColliders.push(left, right, top);
    const pillarOffset = 3.1 * scale;
    for (const side of [-1, 1]) {
      const local = new THREE.Vector3(pillarOffset * side, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), rotationY);
      this.obstacles.push({
        minX: position.x + local.x - 0.75 * scale,
        maxX: position.x + local.x + 0.75 * scale,
        minZ: position.z + local.z - 0.75 * scale,
        maxZ: position.z + local.z + 0.75 * scale,
        minY: position.y,
        maxY: position.y + 8 * scale,
      });
    }
  }

  private createWorkshop(): void {
    const base = surfacePosition("copperwing-workshop", -90, -29, 0);
    const platform = new THREE.Mesh(new THREE.CylinderGeometry(11, 12.5, 1.2, 20), this.darkMetalMaterial);
    platform.position.copy(base).add(new THREE.Vector3(0, 0.6, 0));
    platform.castShadow = true;
    platform.receiveShadow = true;
    this.root.add(platform);

    for (let index = 0; index < 3; index += 1) {
      const radius = 5.2 + index * 2.7;
      const gear = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.42, 8, 36), index === 1 ? this.copperMaterial : this.darkMetalMaterial);
      gear.position.copy(base).add(new THREE.Vector3(0, 7 + index * 2.4, 0));
      gear.rotation.set(Math.PI / 2, index * 0.4, 0);
      gear.castShadow = true;
      this.root.add(gear);
      this.spinners.push({ object: gear, axis: "z", speed: (index % 2 === 0 ? 1 : -1) * (0.12 + index * 0.04) });
    }

    const chimneys = [
      [-105, -20],
      [-75, -34],
      [-99, -45],
    ] as const;
    chimneys.forEach(([x, z], index) => {
      const ground = this.groundHeightAt(x, z) ?? 11;
      const chimney = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.7, 7 + index, 8), this.darkMetalMaterial);
      chimney.position.set(x, ground + (7 + index) / 2, z);
      chimney.castShadow = true;
      this.root.add(chimney);
    });
  }

  private createSanctum(): void {
    const center = surfacePosition("stargazer-sanctum", 5, -96, 0.1);
    const dais = new THREE.Mesh(new THREE.CylinderGeometry(11, 12, 1.4, 32), this.paleStoneMaterial);
    dais.position.copy(center).add(new THREE.Vector3(0, 0.7, 0));
    dais.castShadow = true;
    dais.receiveShadow = true;
    this.root.add(dais);
    this.cameraColliders.push(dais);

    for (let index = 0; index < 3; index += 1) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(5.8 + index * 2.7, 0.22 + index * 0.05, 8, 64),
        index === 1 ? this.copperMaterial : this.darkMetalMaterial,
      );
      ring.position.copy(center).add(new THREE.Vector3(0, 2.2 + index * 1.35, 0));
      ring.rotation.set(Math.PI / 2 - index * 0.24, index * 0.45, 0);
      ring.castShadow = true;
      this.root.add(ring);
      this.spinners.push({ object: ring, axis: "z", speed: index % 2 === 0 ? 0.06 : -0.08 });
    }

    for (let index = 0; index < 8; index += 1) {
      const angle = (index / 8) * Math.PI * 2;
      const pillar = new THREE.Mesh(new THREE.BoxGeometry(1.05, 8.2, 1.05), this.paleStoneMaterial);
      pillar.position.copy(center).add(new THREE.Vector3(Math.cos(angle) * 15, 4.1, Math.sin(angle) * 11));
      pillar.rotation.y = -angle;
      pillar.castShadow = true;
      pillar.receiveShadow = true;
      this.root.add(pillar);
      this.cameraColliders.push(pillar);
    }
  }

  private createCoreTowers(): void {
    const definitions = [
      { id: "core-meadow", label: "草原風核塔", position: surfacePosition("windward-meadow", 1, -8, 0) },
      { id: "core-garden", label: "花園風核塔", position: surfacePosition("hanging-gardens", 97, -32, 0) },
      { id: "core-workshop", label: "工坊風核塔", position: surfacePosition("copperwing-workshop", -90, -29, 1.35) },
    ];
    definitions.forEach((definition) => this.createCoreTower(definition.id, definition.label, definition.position));
  }

  private createCoreTower(id: string, label: string, position: THREE.Vector3): void {
    const group = new THREE.Group();
    group.position.copy(position);
    group.name = label;

    const base = new THREE.Mesh(new THREE.CylinderGeometry(4.3, 5.2, 1.2, 12), this.paleStoneMaterial);
    base.position.y = 0.6;
    group.add(base);

    for (let index = 0; index < 3; index += 1) {
      const angle = (index / 3) * Math.PI * 2;
      const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.75, 5.3, 0.75), this.paleStoneMaterial);
      pillar.position.set(Math.cos(angle) * 2.4, 3.5, Math.sin(angle) * 2.4);
      pillar.rotation.z = Math.sin(angle) * -0.18;
      pillar.rotation.x = Math.cos(angle) * 0.18;
      group.add(pillar);
    }

    const rings: THREE.Mesh[] = [];
    for (let index = 0; index < 3; index += 1) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(2.2 + index * 0.55, 0.16, 8, 42),
        index === 1 ? this.copperMaterial.clone() : this.darkMetalMaterial.clone(),
      );
      ring.position.y = 6.5;
      ring.rotation.set(index * 0.5, index * 0.85, 0);
      group.add(ring);
      rings.push(ring);
    }

    const crystalMaterial = new THREE.MeshStandardMaterial({
      color: 0x688b87,
      emissive: 0x173d3c,
      emissiveIntensity: 0.55,
      roughness: 0.24,
      metalness: 0.16,
      transparent: true,
      opacity: 0.95,
    });
    const crystal = new THREE.Mesh(new THREE.OctahedronGeometry(1.35, 0), crystalMaterial);
    crystal.position.y = 6.5;
    crystal.scale.y = 1.5;
    group.add(crystal);

    const glow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: createGlowTexture(),
        color: 0x6ce5d0,
        transparent: true,
        opacity: 0.09,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    glow.position.y = 6.5;
    glow.scale.setScalar(8.5);
    group.add(glow);

    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.3, 1.25, 52, 16, 1, true),
      new THREE.MeshBasicMaterial({
        color: 0x77f7dc,
        transparent: true,
        opacity: 0.12,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    beam.position.y = 31;
    beam.visible = false;
    group.add(beam);

    const light = new THREE.PointLight(0x72eed6, 0, 24, 2);
    light.position.y = 6.5;
    group.add(light);

    setObjectShadow(group);
    base.receiveShadow = true;
    this.root.add(group);
    this.cameraColliders.push(base, ...group.children.filter((child): child is THREE.Mesh => child instanceof THREE.Mesh && child !== beam));
    this.towers.set(id, {
      id,
      label,
      group,
      position: position.clone(),
      rings,
      crystal,
      glow,
      light,
      beam,
      active: false,
    });
    this.obstacles.push({
      minX: position.x - 3.2,
      maxX: position.x + 3.2,
      minZ: position.z - 3.2,
      maxZ: position.z + 3.2,
      minY: position.y,
      maxY: position.y + 8.5,
    });
  }

  private setTowerActive(tower: AnimatedTower, active: boolean): void {
    tower.active = active;
    const crystalMaterial = tower.crystal.material as THREE.MeshStandardMaterial;
    crystalMaterial.color.set(active ? 0x8ff9df : 0x688b87);
    crystalMaterial.emissive.set(active ? 0x28c8ae : 0x173d3c);
    crystalMaterial.emissiveIntensity = active ? 2.6 : 0.55;
    const glowMaterial = tower.glow.material as THREE.SpriteMaterial;
    glowMaterial.opacity = active ? 0.72 : 0.09;
    tower.light.intensity = active ? 3.4 : 0;
    tower.beam.visible = active;
    tower.rings.forEach((ring) => {
      const material = ring.material as THREE.MeshStandardMaterial;
      material.emissive.set(active ? 0x167f75 : 0x000000);
      material.emissiveIntensity = active ? 0.8 : 0;
    });
  }

  private createShards(): void {
    const definitions = [
      ["shard-01", "測繪員的第一封信", "windward-meadow", -38, 19],
      ["shard-02", "風核的校準紀錄", "windward-meadow", 27, 31],
      ["shard-03", "聚落的晚餐清單", "windward-meadow", 42, -4],
      ["shard-04", "遠航者的銅製羅盤", "windward-meadow", -22, -32],
      ["shard-05", "花園灌溉圖", "hanging-gardens", 79, -22],
      ["shard-06", "懸瀑植物手冊", "hanging-gardens", 115, -43],
      ["shard-07", "工坊值班牌", "copperwing-workshop", -105, -17],
      ["shard-08", "銅翼軸承草圖", "copperwing-workshop", -72, -42],
      ["shard-09", "星盤缺角", "stargazer-sanctum", -6, -88],
      ["shard-10", "最後的風向紀錄", "stargazer-sanctum", 18, -106],
    ] as const;

    definitions.forEach(([id, label, islandId, x, z], index) => {
      const position = surfacePosition(islandId, x, z, 1.65);
      const group = new THREE.Group();
      group.position.copy(position);
      const material = new THREE.MeshStandardMaterial({
        color: 0xb5ffeb,
        emissive: 0x45d9c1,
        emissiveIntensity: 2.1,
        roughness: 0.2,
        metalness: 0.28,
        transparent: true,
        opacity: 0.92,
      });
      const crystal = new THREE.Mesh(new THREE.OctahedronGeometry(0.62, 0), material);
      crystal.scale.set(0.75, 1.45, 0.75);
      const glow = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: createGlowTexture(),
          color: 0x78f3dc,
          transparent: true,
          opacity: 0.52,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      );
      glow.scale.setScalar(3.7);
      group.add(crystal, glow);
      this.root.add(group);
      this.shards.set(id, {
        id,
        label,
        group,
        crystal,
        position: position.clone(),
        collected: false,
        phase: index * 0.83,
      });
    });
  }

  private createCloudLayer(density: number): THREE.Points {
    const random = mulberry32(this.dailySeed ^ 0xc10d);
    const count = Math.floor(280 * THREE.MathUtils.clamp(density, 0.4, 1));
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);

    for (let index = 0; index < count; index += 1) {
      const radius = 55 + Math.sqrt(random()) * 310;
      const angle = random() * Math.PI * 2;
      positions[index * 3] = Math.cos(angle) * radius + (random() - 0.5) * 28;
      positions[index * 3 + 1] = -18 + random() * 28 + (index % 9 === 0 ? 75 + random() * 35 : 0);
      positions[index * 3 + 2] = Math.sin(angle) * radius + (random() - 0.5) * 28;
      sizes[index] = 26 + random() * 44;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("size", new THREE.BufferAttribute(sizes, 1));
    const material = new THREE.PointsMaterial({
      map: createCloudTexture(),
      color: 0xfff9df,
      size: 45,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
      alphaTest: 0.015,
      blending: THREE.NormalBlending,
    });
    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    points.renderOrder = -1;
    return points;
  }

  private createMotes(): THREE.Points {
    const random = mulberry32(this.dailySeed ^ 0x71ae);
    const count = 420;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const warm = new THREE.Color(0xffe5a3);
    const cool = new THREE.Color(0x9affdf);
    for (let index = 0; index < count; index += 1) {
      positions[index * 3] = (random() - 0.5) * 220;
      positions[index * 3 + 1] = random() * 72 - 4;
      positions[index * 3 + 2] = (random() - 0.5) * 210;
      const color = random() > 0.68 ? warm : cool;
      colors[index * 3] = color.r;
      colors[index * 3 + 1] = color.g;
      colors[index * 3 + 2] = color.b;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    const material = new THREE.PointsMaterial({
      color: 0xffffff,
      vertexColors: true,
      size: 0.16,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    return new THREE.Points(geometry, material);
  }

  private createFlyingLife(): void {
    const wingMaterial = new THREE.MeshBasicMaterial({ color: 0x2e525a, side: THREE.DoubleSide });
    for (let flock = 0; flock < 4; flock += 1) {
      const orbit = new THREE.Group();
      orbit.rotation.y = flock * 1.55;
      const bird = new THREE.Group();
      bird.position.set(62 + flock * 14, 35 + flock * 8, 0);
      const leftWing = new THREE.Mesh(new THREE.BufferGeometry(), wingMaterial);
      const rightWing = new THREE.Mesh(new THREE.BufferGeometry(), wingMaterial);
      leftWing.geometry.setAttribute("position", new THREE.Float32BufferAttribute([0, 0, 0, -2.2, 0.25, 0.4, -0.35, 0, 0.7], 3));
      rightWing.geometry.setAttribute("position", new THREE.Float32BufferAttribute([0, 0, 0, 2.2, 0.25, 0.4, 0.35, 0, 0.7], 3));
      leftWing.geometry.computeVertexNormals();
      rightWing.geometry.computeVertexNormals();
      bird.add(leftWing, rightWing);
      orbit.add(bird);
      this.root.add(orbit);
      this.spinners.push({ object: orbit, axis: "y", speed: 0.035 + flock * 0.006 });
    }
  }
}
